import { visit, SKIP } from 'unist-util-visit';
import { createSlugger } from './headings';

/*
 * Veritage markdown extensions (on top of GFM), all via remark-directive:
 *
 *   :::callout{type=info|warn|tip|danger}  … :::     coloured note
 *   :::steps   ### Step title …            :::      numbered steps (each ### starts a step)
 *   :::cards   - [Title](link) — description  :::   link cards grid
 *   ::endpoint{method=POST path="/api/v1/x" auth="X-Api-Key"}
 *   :badge[Required]      inline pill
 *   :method[POST]         inline HTTP method pill
 *
 * Unknown directives are turned back into plain text so stray colons never vanish.
 */

const CONTAINERS = new Set(['callout', 'steps', 'cards']);
const LEAVES = new Set(['endpoint']);
const TEXTS = new Set(['badge', 'method']);

function toText(node) {
  if (node.type === 'text' || node.type === 'inlineCode') return node.value;
  return (node.children || []).map(toText).join('');
}

function restoreAsText(node) {
  const label = (node.children || []).map(toText).join('');
  const prefix = node.type === 'containerDirective' ? ':::' : node.type === 'leafDirective' ? '::' : ':';
  return { type: 'text', value: `${prefix}${node.name}${label ? `[${label}]` : ''}` };
}

function element(node, hName, hProperties = {}) {
  node.data = { ...(node.data || {}), hName, hProperties };
}

export default function remarkVeritage() {
  return (tree) => {
    const slug = createSlugger();

    visit(tree, (node, index, parent) => {
      // Heading ids for anchors / TOC
      if (node.type === 'heading') {
        const id = slug(toText(node));
        node.data = { ...(node.data || {}), hProperties: { ...(node.data?.hProperties || {}), id } };
        return;
      }

      const isDirective = node.type === 'containerDirective' || node.type === 'leafDirective' || node.type === 'textDirective';
      if (!isDirective) return;

      const attrs = node.attributes || {};
      const known =
        (node.type === 'containerDirective' && CONTAINERS.has(node.name)) ||
        (node.type === 'leafDirective' && LEAVES.has(node.name)) ||
        (node.type === 'textDirective' && TEXTS.has(node.name));

      if (!known) {
        if (parent && index != null) {
          parent.children.splice(index, 1, restoreAsText(node));
          return [SKIP, index];
        }
        return;
      }

      switch (node.name) {
        case 'callout':
          element(node, 'vt-callout', { type: attrs.type || 'info', title: attrs.title });
          break;

        case 'steps': {
          // Group children into steps, each beginning at a heading.
          const steps = [];
          let current = null;
          for (const child of node.children) {
            if (child.type === 'heading') {
              current = { type: 'vtStep', data: { hName: 'vt-step' }, children: [] };
              steps.push(current);
              child.data = { hName: 'vt-step-title' };
              current.children.push(child);
            } else {
              if (!current) {
                current = { type: 'vtStep', data: { hName: 'vt-step' }, children: [] };
                steps.push(current);
              }
              current.children.push(child);
            }
          }
          node.children = steps;
          element(node, 'vt-steps');
          break;
        }

        case 'cards':
          // "- [Title](href) — description" → card with title link + description
          visit(node, 'listItem', (li) => {
            const para = li.children?.[0];
            if (!para || para.type !== 'paragraph') return;
            const [first, second, ...rest] = para.children;
            if (first?.type === 'link' && second?.type === 'text') {
              second.value = second.value.replace(/^\s*[—–-]\s*/, '');
              para.children = [first, { type: 'vtCardDesc', data: { hName: 'span', hProperties: { className: 'docs-card-desc' } }, children: [second, ...rest] }];
            }
          });
          element(node, 'div', { className: 'docs-cards' });
          break;

        case 'endpoint':
          element(node, 'vt-endpoint', { method: attrs.method || 'GET', path: attrs.path || '', auth: attrs.auth });
          node.children = [];
          break;

        case 'badge':
          element(node, 'span', { className: `docs-badge docs-badge--${attrs.variant || 'required'}` });
          break;

        case 'method': {
          const m = toText(node).trim().toLowerCase();
          element(node, 'span', { className: `docs-method docs-method--${m}` });
          break;
        }

        default:
          break;
      }
    });
  };
}
